import { useCallback, useEffect, useRef, useState } from "react";
import type { TFunction } from "i18next";
import type { AuthUser } from "../../context/AuthContext";
import type { EventStatistics, EventSummary } from "../../services/drinkService";
import { fetchEventStatistics } from "../../services/drinks/StatsService";
import { fetchAllEvents, fetchOpenEvents } from "../../services/drinks/EventService";
import type { OngoingEventLeaderboardEntry } from "./eventStats/OngoingEventsCard";

const fetchOngoingLeaderboards = async (user: AuthUser): Promise<OngoingEventLeaderboardEntry[]> => {
    const openEvents = await fetchOpenEvents(user);
    const entries: Array<OngoingEventLeaderboardEntry | null> = await Promise.all(openEvents.map(async (event) => {
        try {
            const stats = await fetchEventStatistics(event.eventId, user);
            if (!stats.users.length) return null;
            return {
                event: { eventId: event.eventId, eventName: event.eventName, start: event.start },
                topUsers: stats.users.slice(0, 3),
                totalAlcoholGrams: stats.users.reduce((sum, participant) => sum + participant.totalAlcoholGrams, 0),
            };
        } catch {
            return null;
        }
    }));
    return entries.filter((entry): entry is OngoingEventLeaderboardEntry => entry !== null);
};

export const useEventStatsLoading = (isAuthenticated: boolean, user: AuthUser | null, t: TFunction) => {
    const tRef = useRef(t);
    tRef.current = t;
    const [events, setEvents] = useState<EventSummary[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
    const [stats, setStats] = useState<EventStatistics | null>(null);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingStats, setLoadingStats] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [ongoingLeaderboards, setOngoingLeaderboards] = useState<OngoingEventLeaderboardEntry[]>([]);
    const [loadingOngoing, setLoadingOngoing] = useState(false);
    const statsRequest = useRef(0);

    const loadStats = useCallback(async (eventId: number) => {
        const request = ++statsRequest.current;
        setLoadingStats(true);
        setError(null);
        try {
            const nextStats = await fetchEventStatistics(eventId, user ?? undefined);
            if (request === statsRequest.current) setStats(nextStats);
        } catch {
            if (request === statsRequest.current) {
                setError(tRef.current("eventStats.messages.loadStatsError"));
                setStats(null);
            }
        } finally {
            if (request === statsRequest.current) setLoadingStats(false);
        }
    }, [user]);

    const handleEventSelect = useCallback((eventId: number | null) => {
        if (eventId === null) {
            statsRequest.current++;
            setSelectedEventId(null);
            setStats(null);
            setLoadingStats(false);
            return;
        }
        setSelectedEventId(eventId);
        void loadStats(eventId);
    }, [loadStats]);

    const loadEvents = useCallback(async () => {
        setLoadingEvents(true);
        setError(null);
        try {
            const participated = (await fetchAllEvents(user ?? undefined)).filter((event) => event.isParticipant);
            setEvents(participated);
            if (!participated.length) {
                setSelectedEventId(null);
                setStats(null);
                return;
            }
            const preferred = participated.find((event) => event.isActive) ?? participated.slice()
                .sort((a, b) => (b.start?.getTime() ?? 0) - (a.start?.getTime() ?? 0))[0];
            setSelectedEventId(preferred.eventId);
            await loadStats(preferred.eventId);
        } catch {
            setError(tRef.current("eventStats.messages.loadEventsError"));
            setEvents([]);
            setSelectedEventId(null);
            setStats(null);
        } finally {
            setLoadingEvents(false);
        }
    }, [loadStats, user]);

    const loadOngoing = useCallback(async () => {
        if (!user) {
            setOngoingLeaderboards([]);
            return;
        }
        setLoadingOngoing(true);
        try {
            setOngoingLeaderboards(await fetchOngoingLeaderboards(user));
        } catch {
            setError(tRef.current("eventStats.messages.loadEventsError"));
        } finally {
            setLoadingOngoing(false);
        }
    }, [user]);

    useEffect(() => {
        if (isAuthenticated) loadEvents();
        else {
            setEvents([]);
            setStats(null);
            setSelectedEventId(null);
        }
    }, [isAuthenticated, loadEvents]);
    useEffect(() => {
        if (isAuthenticated) loadOngoing();
        else setOngoingLeaderboards([]);
    }, [isAuthenticated, loadOngoing]);

    return { events, setEvents, selectedEventId, setSelectedEventId, stats, setStats,
        loadingEvents, loadingStats, error, setError, ongoingLeaderboards, loadingOngoing,
        loadEvents, loadOngoing, handleEventSelect };
};
